import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateInsuranceCommissionSettlementPayload,
  InsuranceApiService,
  InsuranceCommissionSettlementListItem,
  InsuranceProducerListItem
} from './insurance-api.service';
import {
  INSURANCE_LIST_TOOLBAR_STYLES,
  SortOrder,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeInsuranceOperationsLiveUpdates,
  toggleSortField
} from './insurance-list-page.utils';

type SettlementSortField = 'settlementNumber' | 'totalAmount' | 'status' | 'paidAtUtc';

@Component({
  standalone: true,
  selector: 'ain-insurance-commission-settlements',
  imports: [
    AinAmountPipe,
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    PartySelectorComponent,
    CurrencySelectorComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule
  ],
  template: `
    <ain-page
      [title]="t('insurance.commissions.settlements.title')"
      [description]="t('insurance.commissions.settlements.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.commissions.settlements.help.page.title'"
        [descriptionKey]="'insurance.commissions.settlements.help.page.description'"
        [bulletKeys]="[
          'insurance.commissions.settlements.help.page.bullets.scope',
          'insurance.commissions.settlements.help.page.bullets.workflow',
          'insurance.commissions.settlements.help.page.bullets.selection'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.commissions.actions.createSettlement')" (click)="openCreateDialog()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.commissions.settlements.help.listFilters.title'"
                [descriptionKey]="'insurance.commissions.settlements.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.commissions.settlements.help.listFilters.bullets.producer',
                  'insurance.commissions.settlements.help.listFilters.bullets.apply'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('insurance.commissions.settlements.filters.producer') }}</span>
                <p-select
                  [options]="producerOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="producerId"
                  name="producerId"
                  [showClear]="true"
                  [filter]="true"
                  appendTo="body" />
              </label>
              <div class="actions">
                <button pButton type="button" [label]="t('common.apply')" (click)="load()"></button>
              </div>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.commissions.settlements.states.empty')" [message]="t('insurance.commissions.settlements.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('settlementNumber')">{{ t('insurance.commissions.settlements.fields.number') }}</button></th>
                  <th>{{ t('insurance.commissions.settlements.fields.producer') }}</th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('totalAmount')">{{ t('insurance.commissions.settlements.fields.totalAmount') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('status')">{{ t('columns.status') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('paidAtUtc')">{{ t('insurance.commissions.settlements.fields.paidAt') }}</button></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.settlementNumber }}</button></td>
                  <td>{{ producerLabel(row.producerId) }}</td>
                  <td>{{ row.totalAmount | ainAmount }} {{ row.currencyCode }}</td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                  <td>{{ row.paidAtUtc ? (row.paidAtUtc | ainDate) : '-' }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }

      <p-dialog
        [header]="t('insurance.commissions.settlementDetail.createTitle')"
        [modal]="true"
        [(visible)]="createDialogVisible"
        [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span>{{ t('insurance.commissions.settlementDetail.fields.legalEntity') }}</span>
            <ain-party-selector [(ngModel)]="createForm.legalEntityId" name="createLegalEntityId" />
          </label>
          <label>
            <span>{{ t('insurance.commissions.settlementDetail.fields.producer') }}</span>
            <p-select
              [options]="producerOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="createForm.producerId"
              name="createProducerId"
              [filter]="true"
              appendTo="body" />
          </label>
          <label>
            <span>{{ t('insurance.commissions.settlementDetail.fields.period') }}</span>
            <input pInputText [(ngModel)]="createForm.period" name="createPeriod" [placeholder]="t('insurance.commissions.settlementDetail.periodPlaceholder')" />
          </label>
          <label>
            <span>{{ t('insurance.commissions.settlementDetail.fields.currencyCode') }}</span>
            <ain-currency-selector [(ngModel)]="createForm.currencyCode" name="createCurrencyCode" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="creating()" (click)="saveCreate()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid label { display: grid; gap: 6px; }
    .dialog-grid .wide { grid-column: 1 / -1; }
    .actions { display: flex; align-items: end; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .filter-grid, .dialog-grid { grid-template-columns: 1fr; } }
    ${INSURANCE_LIST_TOOLBAR_STYLES}
  `]
})
export class InsuranceCommissionSettlementsComponent implements OnInit, OnDestroy {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveUnsubscribe: (() => Promise<void>) | null = null;

  readonly loading = signal(true);
  readonly creating = signal(false);
  readonly error = signal('');
  readonly info = signal('');
  readonly permissionDenied = signal(false);
  readonly rows = signal<InsuranceCommissionSettlementListItem[]>([]);
  readonly selectedRows = signal<InsuranceCommissionSettlementListItem[]>([]);
  readonly producers = signal<InsuranceProducerListItem[]>([]);
  readonly search = signal('');
  readonly sortField = signal<SettlementSortField>('settlementNumber');
  readonly sortOrder = signal<SortOrder>(1);
  producerId: string | null = null;
  createDialogVisible = false;

  createForm: CreateInsuranceCommissionSettlementPayload = this.defaultCreateForm();

  liveStatusKey = liveStatusKey;

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.api.listProducers().subscribe({
      next: rows => this.producers.set(rows),
      error: () => this.producers.set([])
    });
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void this.liveUnsubscribe?.();
  }

  load(): void {
    this.loading.set(true);
    this.api.listCommissionSettlements(this.producerId || null).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.error.set('');
        this.info.set('');
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceCommissionSettlementListItem[] {
    const value = this.search().trim().toLowerCase();
    const filtered = !value
      ? this.rows()
      : this.rows().filter(row =>
        row.settlementNumber.toLowerCase().includes(value)
        || this.producerLabel(row.producerId).toLowerCase().includes(value));

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field], order));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  producerOptions(): Array<{ label: string; value: string }> {
    return this.producers().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  producerLabel(producerId: string): string {
    const producer = this.producers().find(item => item.id === producerId);
    return producer ? `${producer.code} - ${producer.name}` : this.t('insurance.commissions.labels.unknownProducer');
  }

  onSelectionChange(selection: InsuranceCommissionSettlementListItem[] | InsuranceCommissionSettlementListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/commission-settlements', id], {
      queryParams: { q: this.search().trim() || null }
    });
  }

  openCreateDialog(): void {
    this.createForm = this.defaultCreateForm();
    this.createDialogVisible = true;
  }

  saveCreate(): void {
    this.creating.set(true);
    this.error.set('');
    this.api.createCommissionSettlement(this.createForm).subscribe({
      next: item => {
        this.creating.set(false);
        this.createDialogVisible = false;
        void this.router.navigate(['/insurance/commission-settlements', item.id]);
      },
      error: () => {
        this.creating.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('insurance.actions.bulkNothingSelected'));
      return;
    }

    this.info.set(this.t('insurance.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('insurance.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'insurance-commission-settlements.csv',
      ['SettlementNumber', 'Producer', 'TotalAmount', 'CurrencyCode', 'Status', 'PaidAtUtc'],
      rows.map(item => [
        item.settlementNumber,
        this.producerLabel(item.producerId),
        item.totalAmount.toString(),
        item.currencyCode,
        item.status,
        item.paidAtUtc ?? ''
      ]));
    this.info.set(this.t('insurance.actions.exportReady'));
  }

  sortBy(field: SettlementSortField): void {
    const next = toggleSortField(field, this.sortField(), this.sortOrder());
    this.sortField.set(next.field);
    this.sortOrder.set(next.order);
  }

  statusKey(status: InsuranceCommissionSettlementListItem['status']): string {
    return `insurance.commissions.settlements.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  private defaultCreateForm(): CreateInsuranceCommissionSettlementPayload {
    return {
      legalEntityId: '',
      producerId: '',
      period: null,
      currencyCode: 'IRR'
    };
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const live = await subscribeInsuranceOperationsLiveUpdates(tenantId, this.liveUpdateService, () => this.load());
    this.liveUnsubscribe = live.unsubscribe;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
